// DGD Native Wishlist System
// localStorage-based. No app dependency.

(function () {
  'use strict';

  // ─── WishlistManager ──────────────────────────────────────────────────────
  class WishlistManager {
    constructor() {
      this._key = 'dgd_wishlist';
    }

    getAll() {
      try { return JSON.parse(localStorage.getItem(this._key) || '[]'); }
      catch { return []; }
    }

    add(item) {
      // item: { variantId, productId, handle, title, image, price }
      const list = this.getAll().filter(i => i.variantId !== item.variantId);
      list.unshift(item);
      localStorage.setItem(this._key, JSON.stringify(list));
      this._dispatch();
    }

    remove(variantId) {
      localStorage.setItem(this._key, JSON.stringify(
        this.getAll().filter(i => i.variantId !== variantId)
      ));
      this._dispatch();
    }

    toggle(item) {
      this.hasVariant(item.variantId) ? this.remove(item.variantId) : this.add(item);
    }

    hasVariant(variantId) {
      return this.getAll().some(i => i.variantId === variantId);
    }

    hasProduct(productId) {
      return this.getAll().some(i => i.productId === productId);
    }

    count() { return this.getAll().length; }

    _dispatch() {
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: this.getAll() }));
    }
  }

  window.dgdWishlist = new WishlistManager();


  // ─── WishlistHeart ────────────────────────────────────────────────────────
  // Usage: <wishlist-heart data-product-id="..." data-variant-id="..." ...>
  // On product cards: checks hasProduct (any variant). Clicking saves first available variant.
  // On PDP: checks hasVariant (specific). Updates when variant changes.
  class WishlistHeart extends HTMLElement {
    connectedCallback() {
      this._render();
      this.querySelector('button').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this._toggle();
      });

      document.addEventListener('wishlist:change', () => this._updateState());

      // On PDP: watch the variant select so heart updates when variant changes
      this._watchVariant();
      this._updateState();
    }

    _render() {
      const label = this._isWishlisted() ? 'Remove from wishlist' : 'Add to wishlist';
      this.innerHTML = `<button class="wishlist-heart-btn" aria-label="${label}" type="button">
        <svg class="wishlist-heart-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
        </svg>
      </button>`;
    }

    _watchVariant() {
      // Only relevant on PDP where a variant select exists
      const select = document.querySelector('variant-selects select, select[name="id"]');
      if (!select) return;
      select.addEventListener('change', () => {
        this.dataset.variantId = select.value;
        this._updateState();
      });
    }

    _toggle() {
      // Sync variantId from live form in case variant changed since mount
      const select = document.querySelector('variant-selects select, select[name="id"]');
      if (select) this.dataset.variantId = select.value;

      const { variantId, productId, handle, title, image, price } = this.dataset;
      window.dgdWishlist.toggle({
        variantId: parseInt(variantId),
        productId: parseInt(productId),
        handle,
        title,
        image: image || '',
        price: parseFloat(price || 0)
      });
    }

    _isWishlisted() {
      // Cards use product-level check; PDP uses variant-level
      if (this.dataset.cardMode) {
        return window.dgdWishlist.hasProduct(parseInt(this.dataset.productId));
      }
      return window.dgdWishlist.hasVariant(parseInt(this.dataset.variantId));
    }

    _updateState() {
      const wishlisted = this._isWishlisted();
      this.classList.toggle('is-wishlisted', wishlisted);
      const btn = this.querySelector('.wishlist-heart-btn');
      if (btn) btn.setAttribute('aria-label', wishlisted ? 'Remove from wishlist' : 'Add to wishlist');
    }
  }

  customElements.define('wishlist-heart', WishlistHeart);


  // ─── WishlistPage ─────────────────────────────────────────────────────────
  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this._render();
      document.addEventListener('wishlist:change', () => this._render());
    }

    _render() {
      const items = window.dgdWishlist.getAll();
      if (!items.length) {
        this.innerHTML = `<div class="wishlist-empty">
          <p>Your wishlist is empty.</p>
          <a href="/collections/all" class="button">Shop All Discs</a>
        </div>`;
        return;
      }
      this.innerHTML = `<div class="wishlist-grid">${items.map(i => this._cardHTML(i)).join('')}</div>`;
      this._bindEvents();
    }

    _cardHTML(item) {
      const price = item.price ? '$' + (item.price / 100).toFixed(2) : '';
      const imgSrc = item.image || '';
      return `<div class="wishlist-card" data-variant-id="${item.variantId}">
        <a href="/products/${item.handle}?variant=${item.variantId}" class="wishlist-card__image">
          ${imgSrc ? `<img src="${imgSrc}" alt="${this._esc(item.title)}" loading="lazy" width="300" height="300">` : ''}
        </a>
        <div class="wishlist-card__info">
          <a href="/products/${item.handle}?variant=${item.variantId}" class="wishlist-card__title">${this._esc(item.title)}</a>
          ${price ? `<span class="wishlist-card__price">${price}</span>` : ''}
        </div>
        <div class="wishlist-card__actions">
          <button class="button full wishlist-card__atc" data-variant-id="${item.variantId}">Add to Cart</button>
          <button class="wishlist-card__remove" data-variant-id="${item.variantId}" aria-label="Remove from wishlist">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      </div>`;
    }

    _bindEvents() {
      this.querySelectorAll('.wishlist-card__atc').forEach(btn => {
        btn.addEventListener('click', async () => {
          btn.disabled = true;
          btn.textContent = 'Adding...';
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.variantId), quantity: 1 }] })
            });
            btn.textContent = 'Added!';
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } catch {
            btn.textContent = 'Error';
          } finally {
            setTimeout(() => { btn.disabled = false; btn.textContent = 'Add to Cart'; }, 2000);
          }
        });
      });

      this.querySelectorAll('.wishlist-card__remove').forEach(btn => {
        btn.addEventListener('click', () => {
          window.dgdWishlist.remove(parseInt(btn.dataset.variantId));
        });
      });
    }

    _esc(str) {
      return (str || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
    }
  }

  customElements.define('wishlist-page', WishlistPage);


  // ─── WishlistCartNudge ────────────────────────────────────────────────────
  // Renders before checkout: wishlisted items not yet in cart
  class WishlistCartNudge extends HTMLElement {
    connectedCallback() {
      this._render();
      document.addEventListener('wishlist:change', () => this._render());
      document.addEventListener('cart:refresh', () => this._render());
    }

    async _render() {
      const wishlistItems = window.dgdWishlist.getAll();
      if (!wishlistItems.length) { this.hidden = true; return; }

      let cartVariantIds = [];
      try {
        const cart = await fetch('/cart.js').then(r => r.json());
        cartVariantIds = cart.items.map(i => i.variant_id);
      } catch { /* cart fetch failed - show all wishlist items */ }

      const notInCart = wishlistItems.filter(i => !cartVariantIds.includes(i.variantId));
      if (!notInCart.length) { this.hidden = true; return; }

      this.hidden = false;
      const display = notInCart.slice(0, 3);
      const extraCount = notInCart.length - display.length;

      this.innerHTML = `<div class="wishlist-nudge">
        <div class="wishlist-nudge__header">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          <span>${notInCart.length} wishlisted ${notInCart.length === 1 ? 'item' : 'items'} not in cart</span>
        </div>
        <div class="wishlist-nudge__items">
          ${display.map(item => `
            <div class="wishlist-nudge__item">
              ${item.image ? `<img src="${item.image}" alt="${item.title.replace(/"/g,'&quot;')}" width="44" height="44" loading="lazy">` : '<div class="wishlist-nudge__no-img"></div>'}
              <span class="wishlist-nudge__title">${item.title}</span>
              <button class="wishlist-nudge__add" data-variant-id="${item.variantId}" type="button">Add</button>
            </div>`).join('')}
          ${extraCount > 0 ? `<a href="/pages/wishlist" class="wishlist-nudge__more">+${extraCount} more &rarr;</a>` : ''}
        </div>
      </div>`;

      this.querySelectorAll('.wishlist-nudge__add').forEach(btn => {
        btn.addEventListener('click', async () => {
          btn.disabled = true;
          btn.textContent = '✓';
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.variantId), quantity: 1 }] })
            });
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } catch { btn.textContent = '!'; }
        });
      });
    }
  }

  customElements.define('wishlist-cart-nudge', WishlistCartNudge);

  // ─── Header wishlist dropdown ─────────────────────────────────────────────
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function renderDropdown(items) {
    const wrap = document.querySelector('.wishlist-header-wrap');
    if (!wrap) return;

    // Update count badge
    const badge = wrap.querySelector('.wishlist-header-count');
    if (badge) { badge.textContent = items.length; badge.hidden = items.length === 0; }

    const list = wrap.querySelector('.wishlist-dropdown__items');
    const empty = wrap.querySelector('.wishlist-dropdown__empty');
    if (!list || !empty) return;

    empty.hidden = items.length > 0;

    list.innerHTML = items.map(item => `
      <div class="wishlist-dropdown__item" data-variant-id="${item.variantId}">
        <a href="/products/${item.handle}" class="wishlist-dropdown__img-link">
          ${item.image ? `<img src="${item.image}" alt="${item.title}" width="56" height="56" loading="lazy">` : '<div class="wishlist-dropdown__no-img"></div>'}
        </a>
        <div class="wishlist-dropdown__info">
          <a href="/products/${item.handle}" class="wishlist-dropdown__name">${item.title}</a>
          <span class="wishlist-dropdown__price">${money(item.price)}</span>
        </div>
        <div class="wishlist-dropdown__actions">
          <button class="wishlist-dropdown__atc button" data-variant-id="${item.variantId}" type="button">Add to cart</button>
          <button class="wishlist-dropdown__remove" data-variant-id="${item.variantId}" aria-label="Remove" type="button">&#x2715;</button>
        </div>
      </div>
    `).join('');

    // Add to cart
    list.querySelectorAll('.wishlist-dropdown__atc').forEach(btn => {
      btn.addEventListener('click', async () => {
        btn.textContent = '...';
        btn.disabled = true;
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: parseInt(btn.dataset.variantId), quantity: 1 }] })
          });
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          btn.textContent = 'Added!';
          setTimeout(() => { btn.textContent = 'Add to cart'; btn.disabled = false; }, 1500);
        } catch { btn.textContent = 'Error'; btn.disabled = false; }
      });
    });

    // Remove
    list.querySelectorAll('.wishlist-dropdown__remove').forEach(btn => {
      btn.addEventListener('click', () => {
        window.dgdWishlist.remove(parseInt(btn.dataset.variantId));
      });
    });
  }

  // Toggle open/close
  const headerBtn = document.querySelector('.wishlist-header-btn');
  const dropdown = document.querySelector('.wishlist-dropdown');
  if (headerBtn && dropdown) {
    headerBtn.addEventListener('click', e => {
      e.stopPropagation();
      const open = !dropdown.hidden;
      dropdown.hidden = open;
      headerBtn.setAttribute('aria-expanded', String(!open));
    });
    document.addEventListener('click', e => {
      if (!dropdown.hidden && !dropdown.contains(e.target)) {
        dropdown.hidden = true;
        headerBtn.setAttribute('aria-expanded', 'false');
      }
    });
  }

  document.addEventListener('wishlist:change', e => renderDropdown(e.detail));
  renderDropdown(window.dgdWishlist.getAll());

  // Make the entire PDP wishlist pill clickable, not just the heart button
  const pill = document.querySelector('.product-gallery--wishlist');
  if (pill) {
    pill.addEventListener('click', e => {
      const btn = pill.querySelector('.wishlist-heart-btn');
      if (btn && e.target !== btn && !btn.contains(e.target)) {
        btn.click();
      }
    });
  }

})();
